import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "../components/ModalLoadingOverlay";

// ─── Modal box style ───────────────────────────────────────────────
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 600 },
  height: "80vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// ─── Branch-confirm popup style ────────────────────────────────────
const branchPopupStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 4,
  p: 4,
  px: 7,
  textAlign: "center",
};

const editorConfig = {
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "bulletedList",
      "numberedList",
      "blockQuote",
      "insertTable",
      "undo",
      "redo",
    ],
  },
  placeholder: "Write the Description here...",
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// ─── Exact same helpers as Createjobdrawer ─────────────────────────
const sectionLabel = (text) => (
  <Typography sx={{ fontWeight: 500, fontSize: "16px", color: "#000", mb: 1 }}>
    {text}
  </Typography>
);

const fieldLabel = (text, required = false) => (
  <Typography
    sx={{ fontSize: "14px", fontWeight: 400, color: "#000", mb: 0.5 }}
  >
    {text}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </Typography>
);

const cancelBtnSx = {
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontWeight: 500,
  fontSize: "12px",
  borderRadius: "16px",
  minWidth: "132px",
  minHeight: "32px",
  px: 1.5,
  py: 1,
  backgroundColor: "#F8F9FB",
  color: "#6B7280",
  border: "1px solid #D0D5DD",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#F2F4F7",
    border: "1px solid #C3C9D4",
    boxShadow: "none",
  },
};

const primaryBtnSx = {
  backgroundColor: "#3F95EA",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  minWidth: "90px",
  minHeight: "32px",
  px: 1.5,
  py: 1,
  fontSize: "12px",
  "&:hover": { backgroundColor: "#2563EB" },
};

// ─── Tab label component ───────────────────────────────────────────
const StepTab = ({ label, index, activeStep, onTabClick, isClickable }) => {
  const isActive = index === activeStep;
  return (
    <Box
      onClick={() => isClickable && onTabClick(index)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        pt: "10px",
        pb: "10px",
        borderBottom: isActive ? "2px solid #3F95EA" : "2px solid transparent",
        cursor: isClickable ? "pointer" : "default",
        px: 0.5,
        transition: "border-color 0.15s",
        "&:hover":
          isClickable && !isActive ? { borderBottom: "2px solid #90caf9" } : {},
      }}
    >
      <Typography
        fontSize="16px"
        fontWeight={isActive ? 600 : 400}
        color={isActive ? "#3F95EA" : isClickable ? "#6B7280" : "#9CA3AF"}
        noWrap
        sx={{
          transition: "color 0.15s",
          "&:hover": isClickable && !isActive ? { color: "#3F95EA" } : {},
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// ─── Main component ────────────────────────────────────────────────
const AddCompany = ({
  open = true,
  onClose,
  onSuccess,
  disableNavigation = false,
  editCompanyId,
  editMode,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formState, setFormState] = useState({
    name: "",
    url: "",
    site_id: 1,
    phone: "",
    addressLine1: "",
    addressLine2: "",
    state: "",
    city: "",
    zip: "",
    notes: "",
    is_branch_managed: 0,
  });

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Basic Details", "Address", "Additional Info"];

  const [ManageBranchPopup, setManageBranchPopup] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [zipCodes, setZipCodes] = useState([]);
  const [selectedZipCode, setSelectedZipCode] = useState(0);
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});

  const company_id = editCompanyId ?? searchParams.get("c_id");
  const isEdit = editMode ?? searchParams.get("edit") === "1";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));

    if (name === "url") {
      const websiteRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*\/?$/;
      if (value && !websiteRegex.test(value))
        setErrors((prev) => ({ ...prev, url: "Invalid website URL" }));
      else
        setErrors((prev) => {
          const { url, ...rest } = prev;
          return rest;
        });
    }

    if (name === "is_branch_managed") {
      if (value === "1") setManageBranchPopup(true);
      setFormState((prev) => ({ ...prev, is_branch_managed: Number(value) }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    const maxSizeInBytes = 5 * 1024 * 1024;
    if (file && !validImageTypes.includes(file.type)) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please select a valid image file (JPEG, PNG, JPG).");
      setSnackbarOpen(true);
      return;
    }
    if (file?.size > maxSizeInBytes) {
      setSnackbarSeverity("error");
      setSnackbarMessage("File size should not exceed 5 MB.");
      setSnackbarOpen(true);
      return;
    }
    if (file) {
      setFormState((prev) => ({ ...prev, logo: file }));
      setFileName(file?.name);
      setSnackbarSeverity("success");
      setSnackbarMessage("File selected successfully.");
      setSnackbarOpen(true);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
      setErrors((prev) => {
        const { logo, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleStateChange = (event) => {
    const val = event.target.value;
    setSelectedState(val);
    setFormState((prev) => ({ ...prev, state: val, city: "", zip: "" }));
    setSelectedCity("");
    setSelectedZipCode("");
    setZipCodes([]);
  };

  const handleCityChange = (event) => {
    const val = event.target.value;
    setSelectedCity(val);
    setSelectedZipCode("");
    setFormState((prev) => ({ ...prev, city: val }));
    fetchZipCodes(val);
  };

  const handleZipCodeChange = (event) => {
    const val = event.target.value;
    setSelectedZipCode(val);
    setFormState((prev) => ({ ...prev, zip: String(val) }));
  };

  const handleManageBranchCancel = () => {
    setFormState((prev) => ({ ...prev, is_branch_managed: 0 }));
    setManageBranchPopup(false);
  };

  const fetchStates = async () => {
    try {
      const res = await axios.post(
        process.env.REACT_APP_JOBEZEURL + "/state-city/list",
        { search: "" },
      );
      const list = res?.data?.message || [];
      setStates(list.filter((item) => item?.valueType === "state"));
      setAllCities(list.filter((item) => item?.valueType === "city"));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCities = async (stateName) => {
    const selectedStateOption = states.find((state) => state.value === stateName);
    const filteredCities = selectedStateOption?.stateCode
      ? allCities.filter((city) => city.stateCode === selectedStateOption.stateCode)
      : [];
    setCities(filteredCities);
  };

  const fetchZipCodes = async (city) => {
    const selectedCityOption = allCities.find((item) => item.value === city);
    setZipCodes(selectedCityOption?.id ? [{ zip: selectedCityOption.id }] : []);
  };

  const getCompanyDetails = async () => {
    const res = await axios.get(`/company-info/${company_id}`, {
      headers: {
        authorization:
          Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
      },
    });
    const data = res.data.message[0];
    setLogoUrl(data.logo);
    const { logo, ...rest } = data;
    setFormState((prev) => ({ ...prev, ...rest }));
    setFileName(data.logo_name);
    setSelectedState(data.state);
    setSelectedCity(data.city);
    setSelectedZipCode(Number(data.zip));
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchStates();
      if (isEdit) await getCompanyDetails();
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    fetchCities(selectedState);
  }, [allCities, selectedState, states]);

  useEffect(() => {
    fetchZipCodes(selectedCity);
  }, [allCities, selectedCity]);

  const validateStep = (step) => {
    const newErrors = {};
    const websiteRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*\/?$/;
    if (step === 0) {
      if (!formState.name) newErrors.name = "Company Name is required";
      if (!formState.phone) newErrors.phone = "Contact Number is required";
      else if (formState.phone.length !== 10)
        newErrors.phone = "It should contain exactly 10 digits.";
      if (formState.url && !websiteRegex.test(formState.url))
        newErrors.url = "Invalid website URL";
      if (!isEdit && !formState.logo) newErrors.logo = "Logo is required";
    }
    if (step === 1) {
      if (!formState.addressLine1)
        newErrors.addressLine1 = "Address Line 1 is required";
      if (!formState.state) newErrors.state = "State is required";
      if (!formState.city) newErrors.city = "City is required";
    }
    if (step === 2) {
      if (!formState.notes || formState.notes === "<p>&nbsp;</p>" || formState.notes.trim() === "")
        newErrors.notes = "Description is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep((prev) => prev + 1);
  };
  const handleBack = () => {
    setErrors({});
    setActiveStep((prev) => prev - 1);
  };

  const isStepDataFilled = (step) => {
    const websiteRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*\/?$/;
    if (step === 0) {
      return (
        !!formState.name &&
        formState.phone?.length === 10 &&
        (!formState.url || websiteRegex.test(formState.url)) &&
        (isEdit ? true : !!formState.logo)
      );
    }
    if (step === 1)
      return !!formState.addressLine1 && !!formState.state && !!formState.city;
    if (step === 2)
      return !!formState.notes && formState.notes.trim() !== "" && formState.notes !== "<p>&nbsp;</p>";
    return true;
    // if (step === 1)
    //   return !!formState.addressLine1 && !!formState.state && !!formState.city;
    // return true;
  };

  const handleTabClick = (targetStep) => {
    if (targetStep === activeStep) return;
    if (targetStep < activeStep) {
      setErrors({});
      setActiveStep(targetStep);
      return;
    }
    let canNavigate = true;
    for (let i = activeStep; i < targetStep; i++) {
      if (!isStepDataFilled(i)) {
        validateStep(i);
        canNavigate = false;
        break;
      }
    }
    if (canNavigate) {
      setErrors({});
      setActiveStep(targetStep);
    }
  };

  const editCompany = async () => {
    try {
      const fd = new FormData();
      Object.entries({
        name: formState.name,
        url: formState.url,
        phone: formState.phone,
        addressLine1: formState.addressLine1,
        addressLine2: formState.addressLine2,
        state: formState.state,
        city: formState.city,
        zip: formState.zip,
        notes: formState.notes,
        is_branch_managed: formState.is_branch_managed,
        original_date_modified: formState.original_date_modified,
      }).forEach(([k, v]) => fd.append(k, v));
      if (formState?.logo) fd.append("logo", formState.logo);
      const res = await axios.post(`/company/edit/${company_id}`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setSnackbarMessage(res.data?.message);
      return res;
    } catch (err) {
      console.error(err);
      setSnackbarMessage(err?.response?.data?.error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const createCompany = async () => {
    try {
      const fd = new FormData();
      Object.entries({
        name: formState.name,
        url: formState.url,
        phone: formState.phone,
        addressLine1: formState.addressLine1,
        addressLine2: formState.addressLine2,
        state: formState.state,
        city: formState.city,
        zip: formState.zip,
        notes: formState.notes,
        is_branch_managed: formState.is_branch_managed,
      }).forEach(([k, v]) => fd.append(k, v));
      if (formState?.logo) fd.append("logo", formState.logo);
      const res = await axios.post("/company/create", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setSnackbarMessage(res.data?.message?.msg);
      return res;
    } catch (err) {
      console.error(err);
      setSnackbarMessage(err?.response?.data?.error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleResponse = (response) => {
    if (response) {
      setSnackbarSeverity(response.data?.success ? "success" : "error");
      setTimeout(() => setSnackbarOpen(true), 1500);
      setTimeout(() => {
        setLoading(false);
        if (disableNavigation) {
          if (onSuccess) onSuccess();
          if (onClose) onClose();
        } else {
          navigate(-1);
        }
      }, 2000);
    } else setLoading(false);
  };

  const handleClose = () => {
    if (disableNavigation) {
      if (onClose) onClose();
      return;
    }
    navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) return;
    setLoading(true);
    if (isEdit) handleResponse(await editCompany());
    else handleResponse(await createCompany());
  };

  // ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Modal open={open}>
        <Box sx={modalLoadingContainerSx(loading, modalStyle)}>
          {/* ── Modal Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "20px",
              py: "8px",
              borderBottom: "1px solid #E5E7EB",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{ fontSize: "18px", fontWeight: 600, color: "#111827" }}
            >
              {isEdit ? "Edit Company Details" : "Add Company"}
            </Typography>
            <Button
              onClick={handleClose}
              sx={{ minWidth: 0, p: 0.5, color: "#6B7280" }}
            >
              <CloseIcon sx={{ fontSize: "18px" }} />
            </Button>
          </Box>

          {/* ── Tabs ── */}
          <Box
            sx={{
              borderBottom: "1px solid #E5E7EB",
              backgroundColor: "#F9FAFB",
              flexShrink: 0,
              px: "20px",
            }}
          >
            <Box display="flex" gap={3}>
              {steps.map((label, idx) => {
                const canClick =
                  idx !== activeStep &&
                  (() => {
                    for (let i = 0; i < idx; i++) {
                      if (!isStepDataFilled(i)) return false;
                    }
                    return true;
                  })();
                return (
                  <StepTab
                    key={label}
                    label={label}
                    index={idx}
                    activeStep={activeStep}
                    onTabClick={handleTabClick}
                    isClickable={canClick}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ── Scrollable body ── */}
          <Box sx={{ overflowY: "auto", flex: 1, px: 0, py: 0 }}>
            {/* ══ STEP 0 — Basic Details ══ */}
            {activeStep === 0 && (
              <Box display="flex" flexDirection="column" gap={1.5} p={2.5}>
                {sectionLabel("Basic Details")}

                {/* Row 1: Company Name + Contact Number */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("Company Name", true)}
                    <TextField
                      name="name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Enter company name"
                      inputProps={{ maxLength: 60 }}
                      value={formState.name}
                      onChange={handleInputChange}
                      onFocus={() => setErrors((p) => ({ ...p, name: "" }))}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("Contact Number", true)}
                    <TextField
                      name="phone"
                      variant="outlined"
                      fullWidth
                      type="tel"
                      size="small"
                      placeholder="Enter 10-digit number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+1</InputAdornment>
                        ),
                        inputProps: {
                          maxLength: 10,
                          onKeyPress: (e) => {
                            if (e.which > 31 && (e.which < 48 || e.which > 57))
                              e.preventDefault();
                          },
                          pattern: "[0-9]*",
                        },
                      }}
                      value={formState.phone}
                      onChange={handleInputChange}
                      onPaste={(e) => e.preventDefault()}
                      onFocus={() => setErrors((p) => ({ ...p, phone: "" }))}
                      error={!!errors.phone}
                      helperText={errors.phone}
                    />
                  </Box>
                </Box>

                {/* Row 2: Website (full width) */}
                <Box>
                  {fieldLabel("Website")}
                  <TextField
                    name="url"
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder="https://example.com"
                    value={formState.url}
                    onChange={handleInputChange}
                    onFocus={() => setErrors((p) => ({ ...p, url: "" }))}
                    error={!!errors.url}
                    helperText={errors.url}
                  />
                </Box>

                <Divider />

                {/* Logo upload section */}
                {sectionLabel("Company Logo")}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    {fieldLabel("Upload Logo", !isEdit)}
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{
                        borderRadius: "8px",
                        px: 3,
                        py: 1,
                        textTransform: "none",
                        fontSize: "13px",
                        fontWeight: 400,
                        borderColor: errors.logo ? "#ef4444" : "#E5E7EB",
                        color: errors.logo ? "#ef4444" : "#374151",
                      }}
                      startIcon={<FileUploadIcon />}
                    >
                      Upload your logo
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                    </Button>
                    {fileName && (
                      <Typography
                        sx={{ fontSize: "12px", color: "#6B7280", mt: 0.5 }}
                      >
                        {fileName}
                      </Typography>
                    )}
                    {errors.logo && (
                      <Typography
                        sx={{ fontSize: "12px", color: "#ef4444", mt: 0.5 }}
                      >
                        {errors.logo}
                      </Typography>
                    )}
                  </Box>
                  {(logoPreview || logoUrl) && (
                    <img
                      alt="logo"
                      src={logoPreview || logoUrl}
                      style={{
                        width: 72,
                        height: 64,
                        borderRadius: 6,
                        objectFit: "contain",
                        border: "1px solid #E5E7EB",
                      }}
                    />
                  )}
                </Box>

                <Divider />

                {/* Manage Branch section */}
                {sectionLabel("Branch Management")}
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.2,
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#000",
                      flexShrink: 0,
                    }}
                  >
                    Manage branches of this company?
                  </Typography>
                  <FormControl disabled={formState.is_branch_managed === 1}>
                    <RadioGroup
                      row
                      name="is_branch_managed"
                      value={formState.is_branch_managed}
                      onChange={handleInputChange}
                    >
                      <FormControlLabel
                        value={1}
                        label={<Typography fontSize="13px">Yes</Typography>}
                        control={<Radio size="small" />}
                      />
                      <FormControlLabel
                        value={0}
                        label={<Typography fontSize="13px">No</Typography>}
                        control={<Radio size="small" />}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* ══ STEP 1 — Address ══ */}
            {activeStep === 1 && (
              <Box display="flex" flexDirection="column" gap={1.5} p={2.5}>
                {sectionLabel("Address")}

                {/* Address Line 1 */}
                <Box>
                  {fieldLabel("Address Line 1", true)}
                  <TextField
                    name="addressLine1"
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder="Enter address line 1"
                    value={formState.addressLine1}
                    onChange={handleInputChange}
                    onFocus={() =>
                      setErrors((p) => ({ ...p, addressLine1: "" }))
                    }
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1}
                  />
                </Box>

                {/* Address Line 2 */}
                <Box>
                  {fieldLabel("Address Line 2")}
                  <TextField
                    name="addressLine2"
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder="Enter address line 2 (optional)"
                    value={formState.addressLine2}
                    onChange={handleInputChange}
                  />
                </Box>

                <Divider />
                {sectionLabel("State & City")}

                {/* State + City row */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("State", true)}
                    <FormControl fullWidth size="small" error={!!errors.state}>
                      <Select
                        value={selectedState}
                        displayEmpty
                        onChange={handleStateChange}
                        onFocus={() => setErrors((p) => ({ ...p, state: "" }))}
                        renderValue={(v) =>
                          v || (
                            <span style={{ color: "rgba(106,120,133,0.60)" }}>
                              Select
                            </span>
                          )
                        }
                        sx={{ borderRadius: "8px" }}
                      >
                        {states?.map((s) => (
                          <MenuItem key={s.id} value={s.value}>
                            {s.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.state && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#ef4444",
                            px: 1.5,
                            pt: 0.5,
                          }}
                        >
                          {errors.state}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    {fieldLabel("City", true)}
                    <FormControl fullWidth size="small" error={!!errors.city}>
                      <Select
                        value={selectedCity}
                        displayEmpty
                        onChange={handleCityChange}
                        onFocus={() => setErrors((p) => ({ ...p, city: "" }))}
                        disabled={!selectedState}
                        renderValue={(v) =>
                          v || (
                            <span style={{ color: "rgba(106,120,133,0.60)" }}>
                              Select
                            </span>
                          )
                        }
                        sx={{ borderRadius: "8px" }}
                      >
                        {cities?.map((c) => (
                          <MenuItem key={c.id} value={c.value}>
                            {c.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.city && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#ef4444",
                            px: 1.5,
                            pt: 0.5,
                          }}
                        >
                          {errors.city}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                {/* Zip Code (full width or half) */}
                <Box sx={{ width: "50%" }}>
                  {fieldLabel("Zip Code")}
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedZipCode || ""}
                      displayEmpty
                      onChange={handleZipCodeChange}
                      disabled={!selectedCity}
                      renderValue={(v) =>
                        v || (
                          <span style={{ color: "rgba(106,120,133,0.60)" }}>
                            Select
                          </span>
                        )
                      }
                      sx={{ borderRadius: "8px" }}
                    >
                      {zipCodes?.map((z) => (
                        <MenuItem key={z.zip} value={z.zip}>
                          {z.zip}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* ══ STEP 2 — Additional Info ══ */}
            {activeStep === 2 && (
              <Box display="flex" flexDirection="column" gap={1.5} p={2.5}>
                {sectionLabel("Additional Info")}
                {fieldLabel("Notes / Description", true)}
                <Box
  sx={{
    "& .ck-editor__editable": {
      minHeight: "160px !important",
      maxHeight: "200px !important",
      overflowY: "auto !important",
    },
  }}
>
  <CKEditor
    editor={ClassicEditor}
    config={editorConfig}
    data={formState.notes}
    onChange={(_, editor) =>
      setFormState((prev) => ({
        ...prev,
        notes: editor.getData(),
      }))
    }
  />
</Box>
{/* ← YEH ERROR MESSAGE ADD KARO CKEditor ke BAAD */}
{errors.notes && (
  <Typography sx={{ fontSize: "12px", color: "#ef4444", mt: 0.5 }}>
    {errors.notes}
  </Typography>
)}
                {/* <Box
                  sx={{
                    "& .ck-editor__editable": {
                      minHeight: "160px !important",
                      maxHeight: "200px !important",
                      overflowY: "auto !important",
                    },
                  }}
                >
                  <CKEditor
                    editor={ClassicEditor}
                    config={editorConfig}
                    data={formState.notes}
                    onChange={(_, editor) =>
                      setFormState((prev) => ({
                        ...prev,
                        notes: editor.getData(),
                      }))
                    }
                  />
                </Box> */}
              </Box>
            )}
          </Box>

          {/* ── Modal Footer ── */}
          <Box sx={{ flexShrink: 0, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                variant="contained"
                onClick={activeStep === 0 ? handleClose : handleBack}
                sx={cancelBtnSx}
                disableElevation
              >
                {activeStep === 0 ? "Cancel" : "Back"}
              </Button>

              {activeStep < 2 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={primaryBtnSx}
                  disableElevation
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={primaryBtnSx}
                  disableElevation
                >
                  {isEdit ? "Save" : "Add"}
                </Button>
              )}
            </Box>
          </Box>
          <ModalLoadingOverlay open={loading} />
        </Box>
      </Modal>

      {/* ── Branch confirm popup ── */}
      <Modal open={ManageBranchPopup}>
        <Box sx={branchPopupStyle}>
          <Typography variant="h5" fontWeight={500}>
            Do you want to manage branches for this Company?
          </Typography>
          <Typography
            variant="body2"
            fontWeight={400}
            sx={{ mt: 2, color: "#ed6c02" }}
          >
            Once manage branch feature is enabled, It can't be disabled!
          </Typography>
          <Box display="flex" justifyContent="space-evenly" sx={{ mt: 4 }}>
            <Button
              className="cancel-btn"
              variant="outlined"
              sx={{ width: "40%", textTransform: "none" }}
              onClick={handleManageBranchCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                width: "40%",
                textTransform: "none",
                backgroundColor: "#3F95EA",
              }}
              onClick={() => setManageBranchPopup(false)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddCompany;
